import { CalendarTodayOutlined, Check, CheckCircleOutline, EditOutlined, InfoOutlined, LocalOfferOutlined, PhoneAndroidOutlined, PrintOutlined, ReceiptLongOutlined, Remove, RestaurantOutlined, ViewInArOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { Link as RouterLink } from "react-router-dom";
import CabeceraPasoEvento from "../CabeceraPasoEvento";
import { nombreComandera } from "./PasoCobro";
import { cargarDescuentos } from "../../descuentos";
import {
  PASOS, describirVoucher, duracionTexto, formatoFecha, formatoPesos, resumenBloqueo, tieneCatalogo, mensajeErrorEvento,
} from "../../utils";

const enPestanaNueva = { target: "_blank", rel: "noopener noreferrer" };
const fechaCorta = (dia) => dayjs(dia).format("DD/MM");
const precio = (value) => value == null || value === "" ? "—" : formatoPesos(value);
const tablaSx = {
  width: "100%", borderCollapse: "collapse", tableLayout: "fixed", color: "#292341",
  "& th, & td": { fontSize: { xs: 10.5, sm: 11.5 }, py: 0.35, px: 0.5, borderBottom: "1px solid #E7E5F3", textAlign: "left", verticalAlign: "top", overflowWrap: "anywhere" },
  "& th": { fontWeight: 700, color: "#211A46" },
  "& tr:last-child > th, & tr:last-child > td": { borderBottom: 0 },
};

function TarjetaResumen({ titulo, icono: Icono, paso, editar, children, sinRelleno = false }) {
  return (
    <Box component="section" aria-label={titulo} sx={{ minWidth: 0, border: "1px solid #DFDCF2", borderRadius: "11px", overflow: "hidden", background: "linear-gradient(120deg, #fff, #FBFAFF)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.4, minHeight: 29, background: "linear-gradient(100deg, #F1ECFE, #EDE8FB)" }}>
        <Box sx={{ width: 24, height: 24, display: "grid", placeItems: "center", flexShrink: 0, color: "#462BBD", borderRadius: "6px", background: "#EBE5FE" }}><Icono sx={{ fontSize: 18 }} /></Box>
        <Typography component="h3" sx={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: "#221353" }}>{titulo}</Typography>
        {editar && paso != null && <Button variant="outlined" size="small" startIcon={<EditOutlined />} aria-label={"Editar " + titulo} onClick={() => editar(paso)}
          sx={{ minHeight: "21px !important", fontSize: "10px !important", px: 1.1, py: 0, whiteSpace: "nowrap", "& .MuiButton-startIcon": { mr: 0.4, ml: 0, "& svg": { fontSize: 12 } } }}>Editar</Button>}
      </Box>
      <Box sx={{ p: sinRelleno ? 0 : 1.25, fontSize: 11.5, color: "#332C4E", overflowWrap: "anywhere" }}>{children}</Box>
    </Box>
  );
}
TarjetaResumen.propTypes = {
  titulo: PropTypes.string.isRequired, icono: PropTypes.elementType.isRequired, paso: PropTypes.number, editar: PropTypes.func,
  children: PropTypes.node.isRequired, sinRelleno: PropTypes.bool,
};

function TablaDatos({ filas }) {
  return <Box component="table" sx={tablaSx}><colgroup><col style={{ width: "40%" }} /><col /></colgroup><tbody>
    {filas.map(([label, value]) => <tr key={label}><th scope="row">{label}</th><td>{value ?? "—"}</td></tr>)}
  </tbody></Box>;
}
TablaDatos.propTypes = { filas: PropTypes.arrayOf(PropTypes.array).isRequired };

function EtiquetaEstado({ activo, children }) {
  return <Box component="span" sx={{ display: "inline-block", px: 0.8, py: 0.15, borderRadius: "6px", fontSize: 10.5, fontWeight: 700, background: activo ? "#E1F3DB" : "#ECEBF2", color: activo ? "#386C2E" : "#716B86" }}>{children}</Box>;
}
EtiquetaEstado.propTypes = { activo: PropTypes.bool, children: PropTypes.node.isRequired };

const resumenAplicaciones = (config) => config.aplicaciones.map((grupo) => {
  let beneficio;
  if (grupo.usarGeneral) beneficio = (config.general ?? 0) + " %";
  else if (grupo.porcentaje != null && grupo.porcentaje !== "") beneficio = grupo.porcentaje + " %";
  else beneficio = grupo.tipos.filter((tipo) => tipo.porcentaje != null && tipo.porcentaje !== "")
    .map((tipo) => tipo.tipoUsuario + " " + tipo.porcentaje + " %").join(", ") || "0 %";
  return grupo.appUsuario + ": " + beneficio;
}).join(" · ");

export default function PasoRevision({ form, opciones, irAPaso, validar, diagnostico, soloLectura }) {
  const cambiar = soloLectura ? null : irAPaso;
  const medios = (opciones?.mediosPago || []).filter((medio) => !form.mediosPago || form.mediosPago.includes(medio.codigo));
  const comandera = (opciones?.comanderas || []).find((item) => String(item.id) === String(form.idComandera || opciones?.idComanderaPredeterminada));
  const { data, loading, error } = diagnostico;
  const vouchers = data?.vouchersFisicos || [];
  const descuentos = cargarDescuentos(form.descuentosClientes);
  const menuExcluido = descuentos.menuUniversitarioAplicaDescuento === false;
  const hayBloqueo = form.bloqueaAppCompleta || form.bloqueaMenu || form.bloqueaCafeteria;

  const avisos = [];
  // Lo que impide guardar se muestra acá, con el paso donde corregirlo.
  PASOS.slice(0, -1).forEach((paso, index) => {
    Object.values(validar(index)).forEach((texto) => {
      avisos.push({
        severidad: "error",
        texto: <>{texto} <Link component="button" type="button" onClick={() => irAPaso(index)}>Ir a {paso.titulo}</Link></>,
      });
    });
  });
  if (data?.truncado) {
    avisos.push({ severidad: "warning", texto: `Los controles revisan solo los primeros ${data.dias.length} días del evento.` });
  }
  if (data?.eventosSolapados?.length) {
    avisos.push({
      severidad: "error",
      texto: `Se superpone con ${data.eventosSolapados.map((evento) => `«${evento.nombre}» (${formatoFecha(evento.inicioArgentina)} a ${formatoFecha(evento.finArgentina)})`).join(", ")}. Cambie el horario o desactive uno de los dos.`,
    });
  }
  if (!tieneCatalogo(form)) avisos.push({ severidad: "warning", texto: "No hay productos a la venta ni menú universitario." });
  if (form.menuUniversitario && data && !data.categoriaMenuEncontrada) {
    avisos.push({ severidad: "warning", texto: "No existe la categoría «Menú universitario»: no se mostrarán platos." });
  } else if (form.menuUniversitario && data?.turnosActivos === 0) {
    avisos.push({ severidad: "warning", texto: "No hay turnos activos en Villa Mercedes: no se mostrarán platos." });
  }
  if (form.menuUniversitario && data?.diasSinMenu?.length) {
    avisos.push({
      severidad: "warning",
      texto: <>Sin platos en el cronograma para {data.diasSinMenu.map(fechaCorta).join(", ")}. Cárguelos en <Link component={RouterLink} to="/menuUniversitario" {...enPestanaNueva}>Menú universitario</Link>.</>,
    });
  }

  return (
    <Box>
      <CabeceraPasoEvento paso={PASOS.length - 1} mostrarNumero={false}
        titulo={soloLectura ? "Revisar configuración" : "Revisar y confirmar configuración"}
        ayuda="Resumen de todas las selecciones realizadas en los pasos anteriores."
        acciones={<Alert severity="info" icon={<InfoOutlined sx={{ fontSize: 18 }} />}
          sx={{ maxWidth: 470, fontSize: "11px !important", background: "#E8F5FF", color: "#304A68", borderRadius: "9px", "& .MuiAlert-icon": { py: 0.5, mr: 0.75 } }}>
          {soloLectura ? "Consultá la configuración guardada del evento." : "Verificá que la información sea correcta antes de guardar los cambios."}
        </Alert>} />

      {(loading || error || avisos.length > 0) && <Box sx={{ display: "grid", gap: 1, mb: 1.25 }}>
        {loading && <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={16} /><Typography sx={{ fontSize: 12 }}>Verificando configuración…</Typography></Box>}
        {error && <Alert severity="warning">No se pudieron hacer los controles automáticos ({mensajeErrorEvento(error)}). Puede guardar igual.</Alert>}
        {avisos.map((aviso, index) => <Alert key={index} severity={aviso.severidad}>{aviso.texto}</Alert>)}
      </Box>}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}>
        <Box sx={{ display: "grid", alignContent: "start", gap: 1.25, minWidth: 0 }}>
          <TarjetaResumen titulo="Datos del evento" icono={CalendarTodayOutlined} paso={0} editar={cambiar}>
            <TablaDatos filas={[
              ["Nombre del evento", form.nombre],
              ["Inicio", formatoFecha(form.inicio)],
              ["Fin", formatoFecha(form.fin)],
              ["Duración", duracionTexto(form.inicio, form.fin) || "—"],
              ["Estado", <Box key="estado" component="span" sx={{ px: 0.85, py: 0.2, borderRadius: "6px", fontSize: 10.5, color: form.activo ? "#fff" : "#716B86", background: form.activo ? "#65AD50" : "#ECEBF2" }}>{form.activo ? "Activo" : "Inactivo"}</Box>],
            ]} />
          </TarjetaResumen>

          <TarjetaResumen titulo="Menú universitario" icono={RestaurantOutlined} paso={2} editar={cambiar}>
            <TablaDatos filas={[
              ["Vender platos del menú", <EtiquetaEstado key="menu" activo={form.menuUniversitario}>{form.menuUniversitario ? "Sí" : "No"}</EtiquetaEstado>],
              ["Precio de cada plato", !form.menuUniversitario ? "—" : form.menuModoPrecio === "FIJO" ? "Precio fijo" : "Tarifa de cada comprador"],
              ["Precio fijo", form.menuUniversitario && form.menuModoPrecio === "FIJO" ? precio(form.menuUniversitarioPrecio) : "—"],
              ["Descuento de Facturación", form.menuUniversitario && !menuExcluido ? "Aplica" : "No aplica"],
            ]} />
          </TarjetaResumen>

          <TarjetaResumen titulo="Facturación" icono={ReceiptLongOutlined} paso={4} editar={cambiar}>
            <TablaDatos filas={[
              ["Factura", "Factura B"], ["Comunidad UPrO", "Exenta"], ["Particular", "IVA 21 % incluido"],
              ["Descuento general", (descuentos.general ?? 0) + " %"],
              ["Descuentos por tipo de usuario", resumenAplicaciones(descuentos)],
              ["Menú excluido del descuento", menuExcluido ? "Sí" : "No"],
            ]} />
          </TarjetaResumen>
        </Box>

        <Box sx={{ display: "grid", alignContent: "start", gap: 1.25, minWidth: 0 }}>
          <TarjetaResumen titulo="Productos a la venta" icono={ViewInArOutlined} paso={1} editar={cambiar} sinRelleno>
            {form.productos.length ? <Box component="table" aria-label="Resumen de productos" sx={{
              ...tablaSx, "& th, & td": { ...tablaSx["& th, & td"], px: { xs: 0.5, sm: 1 } }, "& thead th": { background: "#F1EDFC", fontSize: { xs: 10, sm: 10.5 }, textAlign: "center" },
              "& thead th:last-child": { overflowWrap: "normal" },
            }}>
              <colgroup><col style={{ width: "44%" }} /><col style={{ width: "22%" }} /><col style={{ width: "22%" }} /><col style={{ width: "12%" }} /></colgroup>
              <thead><tr><th style={{ textAlign: "left" }}>Producto</th><th>Precio de carta</th><th>Precio de venta</th><th>A la venta</th></tr></thead>
              <tbody>{form.productos.map((item) => (
                <tr key={item.idProducto}>
                  <td>{item.producto?.nombre || "#" + item.idProducto}</td>
                  <td style={{ textAlign: "center" }}>{precio(item.producto?.precioCarta)}</td>
                  <td style={{ textAlign: "center" }}>{precio(item.precioEvento)}</td>
                  <td style={{ textAlign: "center" }}><Box component="span" role="img" aria-label={item.habilitado ? "A la venta" : "Pausado"}
                    sx={{ display: "inline-grid", placeItems: "center", width: 17, height: 17, borderRadius: "50%", background: item.habilitado ? "#DDF2DB" : "#ECEBF2", color: item.habilitado ? "#347C39" : "#8B849C" }}>
                    {item.habilitado ? <Check sx={{ fontSize: 13 }} /> : <Remove sx={{ fontSize: 13 }} />}
                  </Box></td>
                </tr>
              ))}</tbody>
            </Box> : <Typography sx={{ p: 1.25, fontSize: 11.5 }}>No hay productos configurados.</Typography>}
          </TarjetaResumen>

          <TarjetaResumen titulo="Cobro e impresión" icono={PrintOutlined} paso={3} editar={cambiar}>
            <TablaDatos filas={[
              ["Medios de pago", medios.map((medio) => medio.etiqueta).join(", ") || "—"],
              ["Impresión", "Comandas en " + (comandera ? nombreComandera(comandera) : "#" + (form.idComandera || opciones?.idComanderaPredeterminada || "—")) + (!form.idComandera ? " (predeterminada)" : "")],
            ]} />
          </TarjetaResumen>

          <TarjetaResumen titulo="Vouchers físicos" icono={LocalOfferOutlined}>
            <Typography sx={{ fontSize: 11.5 }}>{soloLectura ? "No se revisan en un evento finalizado."
              : loading ? "Consultando vouchers…"
                : error ? "No se pudieron consultar los vouchers."
                  : data && !vouchers.length ? "No se administran vouchers físicos en estas fechas." : null}</Typography>
            {vouchers.length > 0 && <Box sx={{ display: "grid", gap: 1 }}>
              {vouchers.map((voucher) => (
                <Box key={voucher.idVoucher} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                  {voucher.problema ? <WarningAmberOutlined sx={{ color: "#C68A24", fontSize: 17 }} /> : <CheckCircleOutline sx={{ color: "#39A077", fontSize: 17 }} />}
                  <Box>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>{voucher.grupo} · {describirVoucher(voucher)}</Typography>
                    <Typography sx={{ fontSize: 11, color: "#77718B" }}>{dayjs(voucher.desde).format("DD/MM")} al {dayjs(voucher.hasta).format("DD/MM")}{voucher.problema && " · " + voucher.problema}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>}
            {!soloLectura && <Box sx={{ mt: 0.5 }}><Link component={RouterLink} to="/beneficios-vouchers" {...enPestanaNueva} sx={{ fontSize: 10.5, color: "#66549C" }}>Administrar vouchers</Link></Box>}
          </TarjetaResumen>

          <TarjetaResumen titulo="App de autogestión" icono={PhoneAndroidOutlined} paso={5} editar={cambiar}>
            {hayBloqueo ? resumenBloqueo(form) : "Funciona normalmente."}
            {hayBloqueo && form.mensajeBloqueo?.trim() && <Typography sx={{ fontSize: 11.5, color: "#77718B", mt: 0.5 }}>«{form.mensajeBloqueo.trim()}»</Typography>}
          </TarjetaResumen>
        </Box>
      </Box>
    </Box>
  );
}

PasoRevision.propTypes = {
  form: PropTypes.object.isRequired,
  opciones: PropTypes.object,
  irAPaso: PropTypes.func.isRequired,
  validar: PropTypes.func,
  soloLectura: PropTypes.bool,
  diagnostico: PropTypes.shape({ data: PropTypes.object, loading: PropTypes.bool, error: PropTypes.object }).isRequired,
};

PasoRevision.defaultProps = { validar: () => ({}), soloLectura: false };
